import { Form, Grid } from 'semantic-ui-react';
import { CREATE_MODE } from '@src/constants';
import ActivityDetailsCreateModeSteps from './ActivityDetailsCreateModeSteps';
import ActivityDetailsContainer from './ActivityDetailsContainer';
import ButtonWrapper from '@components/common/ButtonWrapper';
import styles from './components/styles/Activity.module.scss';
import RiskAssessmentDetailsContainer from './components/riskAssessment/RiskAssessmentDetailsContainer';
import ActionFormContainer from './components/actionForm/ActionFormContainer';
import TraceabilityFormContainer from './components/traceability/TraceabilityFormContainer';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { Loading } from 'react-lib/dist/components-es';
import { ActivityState } from './types/ActivityStates';
import { RiskAssessmentState } from './types/RiskAssessmentState';
import { ActionState } from './types/ActionState';
import { TraceabilityState } from './types/TraceabilityState';
import { ActivityInitialDataResponse } from './types/ActivityInitialDataResponse';

type Props = {
  mode: string;
  currentStep: number;
  setCurrentStep: (arg: number) => void;
  onStepForward: () => void;
  initialData?: ActivityInitialDataResponse;
  activityState: ActivityState;
  riskAssessmentState: RiskAssessmentState;
  actionState: ActionState;
  traceabilityState: TraceabilityState;
  isLoading: boolean;
};

const ActivityForm = ({
  mode,
  currentStep,
  setCurrentStep,
  onStepForward,
  initialData,
  activityState,
  riskAssessmentState,
  actionState,
  traceabilityState,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const methods = useForm({ mode: 'all' });

  const onSubmit = () => {
    onStepForward();
  };

  const handleNextStep = () => {
    methods.handleSubmit(onSubmit)();
  };

  const handlePrevStep = () => {
    if (currentStep == 1) return;
    setCurrentStep(currentStep - 1);
  };

  const steps = [
    {
      step: 1,
      label: t('details.page.step.2.button'),
      component: (
        <ActivityDetailsContainer mode={mode} initialData={initialData} activity={activityState} />
      ),
    },
    {
      step: 2,
      label: t('details.page.step.3.button'),
      component: (
        <RiskAssessmentDetailsContainer riskAssessment={riskAssessmentState} mode={mode} />
      ),
    },
    {
      step: 3,
      label: t('details.page.step.4.button'),
      component: <ActionFormContainer actionState={actionState} initialData={initialData} />,
    },
    {
      step: 4,
      label: t('details.page.save.button'),
      component: (
        <TraceabilityFormContainer
          traceabilityState={traceabilityState}
          initialData={initialData}
          locations={activityState.activity?.location?.map(location => location.location)}
          mode={mode}
        />
      ),
    },
  ];

  const currentStepObject = steps.find(stepObject => stepObject.step === currentStep);

  const handleSetCurrentStep = (step: number) => {
    if (step < currentStep) {
      setCurrentStep(step);
    }
  };

  return (
    <FormProvider {...methods}>
      <Form id="activityDetailsForm">
        <Loading isLoading={isLoading} />
        {mode === CREATE_MODE && (
          <Grid stackable columns={1}>
            <Grid.Column className="detailsGridCol">
              <ActivityDetailsCreateModeSteps
                currentStep={currentStep}
                setCurrentStep={handleSetCurrentStep}
              />
            </Grid.Column>
          </Grid>
        )}

        <Grid columns={2}>
          <Grid.Column tablet={2} computer={4} only="computer tablet" />
          {currentStepObject && (
            <Grid.Column className="detailsGridCol">
              <div className={styles.activityContainer}>{currentStepObject.component}</div>
              <div className={styles.stepButton}>
                {currentStep > 1 && (
                  <ButtonWrapper
                    icon="chevron left"
                    labelPosition="left"
                    color="grey"
                    label={t('details.page.back.button')}
                    key={`step${currentStepObject.step}PrevButtonKey`}
                    onClick={handlePrevStep}
                  />
                )}
                <ButtonWrapper
                  icon="chevron right"
                  labelPosition="right"
                  color="green"
                  label={currentStepObject.label}
                  key={`step${currentStepObject.step}ButtonKey`}
                  onClick={handleNextStep}
                />
              </div>
            </Grid.Column>
          )}
        </Grid>
      </Form>
    </FormProvider>
  );
};

export default ActivityForm;
